import type { SpinMode } from '../core/types';
import { parseJson, type StoragePort } from './storage';

export const CYCLE_JOURNAL_STORAGE_KEY = 'slot-yugioh-rework:season3:v2:cycle-journal';

export interface ValueDecoder<T> { decode(value: unknown): T | null; }

export interface CycleReceiptV2<State, Outcome, ReplayStep> {
  readonly version: 2;
  readonly cycleId: string;
  readonly mode: SpinMode;
  readonly committedAt: number;
  readonly stateBefore: State;
  readonly stateAfter: State;
  readonly outcome: Outcome;
  /** A free-spin replay step is committed in the same localStorage write as the outcome. */
  readonly replayStep: ReplayStep | null;
}

export interface CommitCycleInput<State, Outcome, ReplayStep> {
  readonly cycleId: string;
  readonly mode: SpinMode;
  readonly committedAt: number;
  readonly stateBefore: State;
  readonly stateAfter: State;
  readonly outcome: Outcome;
  readonly replayStep: ReplayStep | null;
}

export interface CycleJournalPort<State, Outcome, ReplayStep> {
  prepare(input: CommitCycleInput<State, Outcome, ReplayStep>): CycleReceiptV2<State, Outcome, ReplayStep>;
  commit(input: CommitCycleInput<State, Outcome, ReplayStep> | CycleReceiptV2<State, Outcome, ReplayStep>): CycleReceiptV2<State, Outcome, ReplayStep>;
  recover(): CycleReceiptV2<State, Outcome, ReplayStep> | null;
  complete(cycleId: string): boolean;
  clear(): void;
}

type UnknownRecord = Record<string, unknown>;

function record(value: unknown): UnknownRecord | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? value as UnknownRecord : null;
}

export class CycleJournal<State, Outcome, ReplayStep> implements CycleJournalPort<State, Outcome, ReplayStep> {
  constructor(
    private readonly storage: StoragePort,
    private readonly stateDecoder: ValueDecoder<State>,
    private readonly outcomeDecoder: ValueDecoder<Outcome>,
    private readonly replayStepDecoder: ValueDecoder<ReplayStep>,
    private readonly key = CYCLE_JOURNAL_STORAGE_KEY,
  ) {}

  prepare(input: CommitCycleInput<State, Outcome, ReplayStep>): CycleReceiptV2<State, Outcome, ReplayStep> {
    if (!/^[A-Za-z0-9_-]{1,128}$/.test(input.cycleId)) throw new TypeError('Invalid cycle id.');
    if (!Number.isSafeInteger(input.committedAt) || input.committedAt <= 0) throw new TypeError('Invalid cycle timestamp.');
    if (input.mode === 'free' && input.replayStep === null) throw new TypeError('Free-spin outcomes require an atomic replay step.');
    const stateBefore = this.stateDecoder.decode(input.stateBefore);
    const stateAfter = this.stateDecoder.decode(input.stateAfter);
    const outcome = this.outcomeDecoder.decode(input.outcome);
    const replayStep = input.replayStep === null ? null : this.replayStepDecoder.decode(input.replayStep);
    if (!stateBefore || !stateAfter || !outcome || (input.replayStep !== null && !replayStep)) {
      const invalidParts = [
        !stateBefore ? 'stateBefore' : null,
        !stateAfter ? 'stateAfter' : null,
        !outcome ? 'outcome' : null,
        input.replayStep !== null && !replayStep ? 'replayStep' : null,
      ].filter((part): part is string => part !== null);
      throw new TypeError(`Refusing to journal an invalid cycle: ${invalidParts.join(', ')}.`);
    }
    const receipt: CycleReceiptV2<State, Outcome, ReplayStep> = {
      version: 2, cycleId: input.cycleId, mode: input.mode, committedAt: input.committedAt,
      stateBefore, stateAfter, outcome, replayStep,
    };
    return receipt;
  }

  commit(
    input: CommitCycleInput<State, Outcome, ReplayStep> | CycleReceiptV2<State, Outcome, ReplayStep>,
  ): CycleReceiptV2<State, Outcome, ReplayStep> {
    const receipt = this.prepare(input);
    // localStorage replaces one key atomically; no economy/replay split is possible here.
    this.storage.setItem(this.key, JSON.stringify(receipt));
    return receipt;
  }

  recover(): CycleReceiptV2<State, Outcome, ReplayStep> | null {
    const source = record(parseJson(this.storage.getItem(this.key)));
    if (!source || source.version !== 2 || typeof source.cycleId !== 'string'
      || !/^[A-Za-z0-9_-]{1,128}$/.test(source.cycleId)
      || (source.mode !== 'base' && source.mode !== 'free')
      || !Number.isSafeInteger(source.committedAt) || Number(source.committedAt) <= 0) {
      this.storage.removeItem(this.key);
      return null;
    }
    const stateBefore = this.stateDecoder.decode(source.stateBefore);
    const stateAfter = this.stateDecoder.decode(source.stateAfter);
    const outcome = this.outcomeDecoder.decode(source.outcome);
    const replayStep = source.replayStep === null ? null : this.replayStepDecoder.decode(source.replayStep);
    if (!stateBefore || !stateAfter || !outcome || (source.mode === 'free' && !replayStep)
      || (source.replayStep !== null && !replayStep)) {
      this.storage.removeItem(this.key);
      return null;
    }
    return {
      version: 2, cycleId: source.cycleId, mode: source.mode, committedAt: Number(source.committedAt),
      stateBefore, stateAfter, outcome, replayStep,
    };
  }

  complete(cycleId: string): boolean {
    const pending = this.recover();
    if (!pending || pending.cycleId !== cycleId) return false;
    this.storage.removeItem(this.key);
    return true;
  }

  clear(): void { this.storage.removeItem(this.key); }
}
